import React from 'react';
import { Link } from 'react-router-dom';
import { Plus, TrendingUp, DollarSign, CreditCard, Target } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import useInitializeFamily from '../../hooks/useInitializeFamily';
import useCategoryStore from '../../store/categoryStore';
import useExpenseStore from '../../store/expenseStore';
import useBudgetStore from '../../store/budgetStore';

const Dashboard = () => {
  // Initialize family data (categories, etc.)
  useInitializeFamily();
  
  const { categories } = useCategoryStore();
  const { expenses, getCurrentMonthExpenses, getTotalExpenses, getRecentExpenses } = useExpenseStore();
  const { currentBudget } = useBudgetStore();
  
  // Get real data
  const currentMonthExpenses = getCurrentMonthExpenses();
  const totalSpent = getTotalExpenses(currentMonthExpenses);
  const recentExpenses = expenses.slice(0, 5); // Get 5 most recent expenses
  
  const stats = {
    totalSpent: totalSpent,
    monthlyBudget: currentBudget?.totalBudget || 0,
    expenseCount: currentMonthExpenses.length,
    savingsGoal: currentBudget?.categories ? 
      Object.values(currentBudget.categories)
        .filter(cat => cat.priority === 'savings')
        .reduce((sum, cat) => sum + cat.budgeted, 0) : 0
  };

  const budgetProgress = stats.monthlyBudget > 0 ? (stats.totalSpent / stats.monthlyBudget) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">Welcome back! Here's your financial overview.</p>
        </div>
        <Link to="/expenses/add">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Add Expense
          </Button>
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Spent</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${stats.totalSpent.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">This month</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Monthly Budget</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${stats.monthlyBudget.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              {budgetProgress.toFixed(0)}% used
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Transactions</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.expenseCount}</div>
            <p className="text-xs text-muted-foreground">This month</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Savings Goal</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${stats.savingsGoal}</div>
            <p className="text-xs text-muted-foreground">Target this month</p>
          </CardContent>
        </Card>
      </div>

      {/* Budget Progress */}
      <Card>
        <CardHeader>
          <CardTitle>Monthly Budget Progress</CardTitle>
          <CardDescription>
            You've spent ${stats.totalSpent} of your ${stats.monthlyBudget} budget
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Progress</span>
              <span>{budgetProgress.toFixed(0)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className={`h-3 rounded-full transition-all duration-300 ${
                  budgetProgress > 90 ? 'bg-red-500' : 
                  budgetProgress > 75 ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${Math.min(budgetProgress, 100)}%` }}
              />
            </div>
            <div className="flex justify-between text-sm text-gray-600">
              <span>Remaining: ${(stats.monthlyBudget - stats.totalSpent).toLocaleString()}</span>
              <span>{Math.max(0, 100 - budgetProgress).toFixed(0)}% left</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Expenses */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Recent Expenses</CardTitle>
              <CardDescription>Your latest transactions</CardDescription>
            </div>
            <Link to="/expenses">
              <Button variant="outline" size="sm">
                View All
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {recentExpenses.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600 mb-4">No expenses yet</p>
              <Link to="/expenses/add">
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Expense
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {recentExpenses.map((expense) => {
                const category = categories.find(cat => cat.id === expense.category);
                const expenseDate = expense.date?.toDate ? expense.date.toDate() : new Date(expense.date);
                
                return (
                  <div key={expense.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3 flex-1">
                      {category && (
                        <div 
                          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm"
                          style={{ backgroundColor: category.color }}
                        >
                          {category.icon}
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{expense.description}</p>
                        <p className="text-sm text-gray-600">
                          {category?.name || 'Unknown'}
                          {expense.subcategory && ` • ${expense.subcategory}`}
                          {' • '}
                          {expenseDate.toLocaleDateString('en-BD')}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">৳{expense.amount.toLocaleString('en-BD')}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
